﻿import type { SampleRuleAction, SampleRuleConditionGroup, SampleRuleConditions } from "@unbox/api";
import { useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { isConditionComplete, makeDefaultCondition } from "./condition-fields";

// No zodResolver here — same reasoning as cohorts' use-rule-draft.ts: validation is
// covered by `canSave` below, and this feature has no backend schema to validate against yet.
export type RuleDraftValues = {
  name: string;
  action: SampleRuleAction;
  conditions: SampleRuleConditions;
  isActive: boolean;
  reprocessPendingRequests: boolean;
};

export type RuleDraftSource = {
  name: string;
  action: SampleRuleAction;
  conditions: SampleRuleConditions;
  isActive: boolean;
};

export const makeConditionGroup = (): SampleRuleConditionGroup => ({
  logic: "all",
  conditions: [makeDefaultCondition()],
});

const emptyDraft = (): RuleDraftValues => ({
  name: "",
  action: "approve",
  conditions: {
    version: 1,
    when: [makeConditionGroup()],
    except: [],
  },
  isActive: true,
  reprocessPendingRequests: false,
});

const draftFromRule = (rule: RuleDraftSource): RuleDraftValues => ({
  name: rule.name,
  action: rule.action,
  conditions: {
    ...rule.conditions,
    when: rule.conditions.when.length > 0 ? rule.conditions.when : [makeConditionGroup()],
  },
  isActive: rule.isActive,
  reprocessPendingRequests: false,
});

export const toCreateRulePayload = (values: RuleDraftValues) => ({
  name: values.name,
  action: values.action,
  conditions: values.conditions,
});

export const toUpdateRulePayload = (values: RuleDraftValues) => ({
  name: values.name,
  conditions: values.conditions,
  isActive: values.isActive,
  reprocessPendingRequests: values.reprocessPendingRequests,
});

export const useRuleDraft = () => {
  const form = useForm<RuleDraftValues>({
    defaultValues: emptyDraft(),
    mode: "onChange",
  });

  const conditions = useWatch({ control: form.control, name: "conditions" });
  const name = useWatch({ control: form.control, name: "name" });
  const action = useWatch({ control: form.control, name: "action" });

  const hydrate = useCallback(
    (rule?: RuleDraftSource) => form.reset(rule ? draftFromRule(rule) : emptyDraft()),
    [form],
  );

  const exceptGroup = conditions?.except[0] ?? null;

  const setExceptGroup = useCallback(
    (group: SampleRuleConditionGroup | null) => {
      form.setValue("conditions.except", group ? [group] : [], { shouldValidate: true, shouldDirty: true });
    },
    [form],
  );

  // Every group must hold at least one condition and every condition a usable value,
  // otherwise the draft would be sent as a rule that matches nothing.
  const conditionsComplete =
    !!conditions &&
    conditions.when.length > 0 &&
    [...conditions.when, ...conditions.except].every(
      (group) => group.conditions.length > 0 && group.conditions.every(isConditionComplete),
    );

  return {
    form,
    conditions,
    exceptGroup,
    setExceptGroup,
    hydrate,
    canSave: name.trim().length > 0 && !!action && conditionsComplete,
  };
};
